import { FC, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Postcode from 'react-daum-postcode';
import { ISchool } from '../../../models/schools.model';

import { Button as AntButton, Modal, Select } from 'antd';
import { useSnackbar } from 'notistack';
import Button from '../../Button';
import useAuth from '../../../hooks/useAuth';
const { Option } = Select;

interface IProps {
  school: ISchool | null;
  onSubmit: Function;
}

const SystemSchoolEditor: FC<IProps> = ({ school, onSubmit }) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const [korName, setKorName] = useState('');
  const [engName, setEngName] = useState<string | undefined>('');
  const [type, setType] = useState<any>();
  const [address, setAddress] = useState('');
  const [detailedAddress, setDetailedAddress] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [tel, setTel] = useState('');
  const [fax, setFax] = useState('');
  const [homepage, setHomepage] = useState('');
  const [note, setNote] = useState('');
  const [postcodeVisible, setPostcodeVisible] = useState(false);

  const korNameInput = useRef<any>();
  const telInput = useRef<any>();

  const typeList = [
    { label: t('system.school.option1'), value: 'ES' },
    { label: t('system.school.option2'), value: 'MS' },
    { label: t('system.school.option3'), value: 'HS' },
  ];

  const onCompletePostcode = (data: any) => {
    setAddress(data.jibunAddress);
    setZipcode(data.zonecode);
    setPostcodeVisible(false);
  };

  useEffect(() => {
    if (school) {
      setKorName(school.schoolName);
      setEngName(school.schoolEngName);
      setType(school.schoolDiv);
      setAddress(school.address);
      setDetailedAddress(school.detailAddress);
      setZipcode(school.zipNo);
      setTel(school.telephoneNo);
      setFax(school.faxNo);
      setHomepage(school.homepageAddress);
      setNote(school.note);
    }
  }, [school]);

  const onSave = async () => {
    if (!korName) {
      korNameInput.current.focus();
      return enqueueSnackbar(t('system.school.message1'), {
        variant: 'info',
      });
    }
    if (!address) {
      return enqueueSnackbar(t('system.school.message2'), {
        variant: 'info',
      });
    }
    if (!tel) {
      telInput.current.focus();
      return enqueueSnackbar(t('system.school.message3'), {
        variant: 'info',
      });
    }
    if (!type) {
      return enqueueSnackbar(t('system.school.message4'), {
        variant: 'info',
      });
    }

    onSubmit({
      schoolName: korName,
      schoolEngName: engName,
      schoolDiv: type,
      address,
      detailAddress: detailedAddress,
      zipNo: zipcode,
      telephoneNo: tel,
      faxNo: fax,
      homepageAddress: homepage,
      note: note,
    });
  };

  return (
    <div className="comm-table__area rowType edit mt-1">
      <table>
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th>* {t('system.school.text1')}</th>
            <td colSpan={3}>
              <input
                type="text"
                className="form-control input-text"
                value={korName ?? ''}
                onChange={(e) => setKorName(e.target.value)}
                ref={korNameInput}
              />
            </td>
          </tr>
          <tr>
            <th>{t('system.school.text2')}</th>
            <td colSpan={3}>
              <input
                type="text"
                className="form-control input-text"
                value={engName ?? ''}
                onChange={(e) => setEngName(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>* {t('system.school.column3')}</th>
            <td colSpan={3}>
              <div className="w-150p">
                <Select
                  className="w-100"
                  placeholder={t('common.button.select')}
                  value={type ?? undefined}
                  onChange={(value) => setType(value)}
                >
                  {typeList?.map((item: any) => {
                    return (
                      <Option key={item.value} value={item.value}>
                        {item.label}
                      </Option>
                    );
                  })}
                </Select>
              </div>
            </td>
          </tr>
          <tr>
            <th>* {t('common.text.address1')}</th>
            <td colSpan={3}>
              <div className="comm-btn__area align-items-center">
                <Button
                  className="btn-basic black"
                  onClick={() => setPostcodeVisible(true)}
                  text={t('common.button.address')}
                />
                {zipcode ? ` (${zipcode})` : ''} {address}
              </div>
            </td>
          </tr>
          <tr>
            <th>{t('common.text.address2')}</th>
            <td colSpan={3}>
              <input
                type="text"
                className="form-control input-text"
                value={detailedAddress ?? ''}
                onChange={(e) => setDetailedAddress(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>* {t('system.school.tel')}</th>
            <td>
              <input
                type="text"
                className="form-control input-text"
                value={tel ?? ''}
                onChange={(e) => setTel(e.target.value)}
                ref={telInput}
              />
            </td>
            <th>{t('common.text.fax')}</th>
            <td>
              <input
                type="text"
                className="form-control input-text"
                value={fax}
                onChange={(e) => setFax(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>{t('system.school.homepage')}</th>
            <td colSpan={3}>
              <input
                type="text"
                className="form-control input-text"
                value={homepage ?? ''}
                onChange={(e) => setHomepage(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>{t('common.text.note')}</th>
            <td colSpan={3}>
              <input
                type="text"
                className="form-control input-text"
                value={note ?? ''}
                onChange={(e) => setNote(e.target.value)}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            onClick={onSave}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={() => history.goBack()}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>

      <Modal
        title={t('common.button.address')}
        visible={postcodeVisible}
        onCancel={() => setPostcodeVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic gray'
              onClick={() => setPostcodeVisible(false)}
            >
              {t('common.button.close')}
            </AntButton>
          </div>
        ]}
      >
        <Postcode onComplete={onCompletePostcode} autoClose={false} />
      </Modal>
    </div>
  );
};

export default SystemSchoolEditor;
